// Family sharing UI -- iteration 077
// Shown on the Profile page. Lets a primary parent create a group, invite
// sub-users (spouse, grandparent, au pair), and add more children.

import { useEffect, useState, type FormEvent } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as api from "@/lib/api";
import type { FamilyGroupPublic } from "@dugads/shared";

const LABEL_SUGGESTIONS = ["Spouse", "Parent", "Grandparent", "Guardian", "Au Pair", "Other"];

export function FamilyCard() {
  const [group, setGroup] = useState<FamilyGroupPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLabel, setInviteLabel] = useState("Spouse");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const [familyCode, setFamilyCode] = useState("");
  const [childSubmitting, setChildSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.getFamilyGroup();
      setGroup(res.group);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load family");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate() {
    setError("");
    try {
      const res = await api.createFamilyGroup();
      setGroup(res.group);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create family group");
    }
  }

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteSubmitting(true);
    setError("");
    try {
      const res = await api.inviteFamilyMember(inviteEmail.trim(), inviteLabel);
      setGroup(res.group);
      setInviteEmail("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to invite member");
    } finally {
      setInviteSubmitting(false);
    }
  }

  async function handleRevoke(memberId: string) {
    if (!confirm("Revoke this member's access?")) return;
    try {
      const res = await api.revokeFamilyMember(memberId);
      setGroup(res.group);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to revoke member");
    }
  }

  async function handleAddChild(e: FormEvent) {
    e.preventDefault();
    if (!familyCode.trim()) return;
    setChildSubmitting(true);
    setError("");
    try {
      const res = await api.addFamilyChild({ family_code: familyCode.trim() });
      setGroup(res.group);
      setFamilyCode("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add child");
    } finally {
      setChildSubmitting(false);
    }
  }

  async function handleRemoveChild(childId: string) {
    if (!confirm("Remove this child from the family group?")) return;
    try {
      const res = await api.removeFamilyChild(childId);
      setGroup(res.group);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to remove child");
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Family</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  if (!group) {
    return (
      <Card data-section="family">
        <CardHeader>
          <CardTitle className="text-base">Family Sharing</CardTitle>
          <CardDescription>
            Share access to your children's school information with family members.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-destructive mb-3">{error}</p>}
          <Button onClick={handleCreate} data-action="family-create">
            Create Family Group
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!group.is_primary) {
    return (
      <Card data-section="family">
        <CardHeader>
          <CardTitle className="text-base">Family: {group.name}</CardTitle>
          <CardDescription>You're a member of this family group</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Children ({group.children.length}) and other members ({group.members.length}) are managed by the
            primary parent.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-section="family">
      <CardHeader>
        <CardTitle className="text-base">{group.name}</CardTitle>
        <CardDescription>
          Invite family members and add children. Members see all children in the group.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Children */}
        <section>
          <h3 className="text-sm font-semibold mb-2">Children ({group.children.length})</h3>
          {group.children.length === 0 ? (
            <p className="text-sm text-muted-foreground mb-3">No children added yet.</p>
          ) : (
            <ul className="space-y-2 mb-3">
              {group.children.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between border rounded-md px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {c.first_name ?? ""} {c.surname ?? ""}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {c.schools.map((s) => s.display_name ?? s.name).join(", ") || "No school"}
                      {c.family_code && ` · code ${c.family_code}`}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveChild(c.id)}
                    data-action="family-remove-child"
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={handleAddChild} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor="family-code" className="text-xs">
                Add child by family code
              </Label>
              <Input
                id="family-code"
                value={familyCode}
                onChange={(e) => setFamilyCode(e.target.value)}
                placeholder="e.g. ABC123"
              />
            </div>
            <Button type="submit" disabled={childSubmitting} data-action="family-add-child">
              {childSubmitting ? "Adding…" : "Add"}
            </Button>
          </form>
        </section>

        {/* Members */}
        <section>
          <h3 className="text-sm font-semibold mb-2">Family Members ({group.members.length})</h3>
          {group.members.length === 0 ? (
            <p className="text-sm text-muted-foreground mb-3">
              No members yet. Invite someone to share access.
            </p>
          ) : (
            <ul className="space-y-2 mb-3">
              {group.members.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between border rounded-md px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {m.display_name ?? m.email}{" "}
                      <span className="text-xs font-normal text-muted-foreground">({m.label})</span>
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {m.email} ·{" "}
                      <span
                        className={
                          m.status === "accepted"
                            ? "text-green-700"
                            : m.status === "revoked"
                            ? "text-destructive"
                            : "text-amber-700"
                        }
                      >
                        {m.status}
                      </span>
                    </div>
                  </div>
                  {m.status !== "revoked" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(m.id)}
                      data-action="family-revoke-member"
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={handleInvite} className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_160px_auto] items-end">
            <div>
              <Label htmlFor="invite-email" className="text-xs">
                Invite by email
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <div>
              <Label htmlFor="invite-label" className="text-xs">
                Relationship
              </Label>
              <select
                id="invite-label"
                value={inviteLabel}
                onChange={(e) => setInviteLabel(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {LABEL_SUGGESTIONS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" disabled={inviteSubmitting} data-action="family-invite">
              {inviteSubmitting ? "Inviting…" : "Invite"}
            </Button>
          </form>
        </section>
      </CardContent>
    </Card>
  );
}
